import { Component, DestroyRef, Input, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { HelpersService } from '@core/helpers/helpers.service';
import { RolesService } from '@core/services/roles.service';
import { selectUserRolesReady } from '@core/state/user/user.selectors';
import { selectIsCollapsed } from '@core/state/userProfile/userProfile.selectors';
import { Store } from '@ngrx/store';
import { MenuItem } from 'primeng/api';
import { MenubarModule } from 'primeng/menubar';
import { combineLatest, filter } from 'rxjs';

export const PARENT_ACTIVE_CLASS = 'p-menubar-item-parent-active';

@Component({
  selector: 'au-header-menu',
  templateUrl: './header-menu.component.html',
  styleUrls: ['./header-menu.component.scss'],
  imports: [RouterLink, MenubarModule],
})
export class HeaderMenuComponent implements OnInit, OnDestroy {
  areUserRolesReady = false;
  @Input() showOnlyDesktop = false;

  constructor(
    private hs: HelpersService,
    private roleService: RolesService,
    private store: Store<any>,
    private router: Router,
    private destroyRef: DestroyRef
  ) {
    this.roleService.loadPermissions();
  }

  items: MenuItem[] = [];
  mercuryLogoPath = '../../assets/images/logo-original.svg';

  ngOnInit(): void {
    combineLatest([
      this.store.select(selectUserRolesReady),
      this.store.select(selectIsCollapsed),
    ]).subscribe((args) => {
      this.areUserRolesReady = args[0];
      this.getLeftMenuItems();
      // Temporary workaround for menu item rendering issue
      // Ensures menu items are correctly displayed using specific intervals
      const refreshDelays = [500, 1500, 4500];
      refreshDelays.forEach((delay) =>
        setTimeout(() => this.getLeftMenuItems(), delay)
      );
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.items = this.markActiveParents(this.items);
      });
  }

  getLeftMenuItems() {
    this.hs.getMenuItems(false).then((items) => {
      this.items = this.markActiveParents(items);
    });
  }

  markActiveParents(items: MenuItem[]): MenuItem[] {
    const currentPath = this.router.url.split(/[?#]/)[0];

    return items.map((item) => {
      if (!item.items?.length) {
        return item;
      }

      const isChildActive = item.items.some((child) =>
        this.isPathUnderLink(currentPath, child.routerLink)
      );

      return {
        ...item,
        styleClass: isChildActive ? PARENT_ACTIVE_CLASS : undefined,
      };
    });
  }

  private isPathUnderLink(path: string, routerLink: unknown): boolean {
    if (typeof routerLink !== 'string' || !routerLink) {
      return false;
    }

    const link = routerLink.startsWith('/') ? routerLink : `/${routerLink}`;
    return path === link || path.startsWith(`${link}/`);
  }

  ngOnDestroy(): void {
    this.items = [];
  }
}
